import { Fragment } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { INK, BODY, MUTED, LINE, GOLD, CYAN, kickerSx, panelSx } from '../GiveawayOffer/giveawayOfferTheme';
import { rewardParts, type PromoWinnerDraw } from './promoPack';

/* Each kind of reward in its own colour, so a row reads at a glance: tokens in
 * the site's cyan, packs in the gold the rewards card uses for them, and the
 * custom reward in a third colour of its own. */
const TOKENS_COLOUR = CYAN;
const PACKS_COLOUR = GOLD;
const CUSTOM_COLOUR = '#c9a4ff';

/* Previous promo winners, under the promo pack: the last draws that have been
 * made, newest first — the week, who won (first name and initial, as the live
 * draw shows them) and what that week rewarded. From GET
 * /package/promotion/winners; honest when there is nothing yet, or the read
 * failed. */

const week = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const PreviousPromoWinners = ({ draws }: { draws: PromoWinnerDraw[] | null | undefined }) => (
  <Box sx={{ width: '100%', maxWidth: 760, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
    <Box component="section" sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 }, minWidth: 0 }}>
      {/* A heading, not a kicker (2026-10-14): in the site's cyan and a size
          above the winners' names, which are ink too, so it stands apart from
          the list it heads; the kicker above it is the page's own label style. */}
      <Typography sx={{ ...kickerSx, mb: 1 }}>The draws so far</Typography>
      <Typography
        component="h2"
        sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 900, lineHeight: 1.08, color: CYAN, mb: 3 }}
      >
        Previous promo winners
      </Typography>

      {draws === undefined ? (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} sx={{ color: CYAN }} aria-label="Loading previous winners" />
        </Box>
      ) : draws === null ? (
        <Typography sx={{ fontSize: 15, color: BODY, lineHeight: 1.6 }}>
          We could not load the previous winners.
        </Typography>
      ) : draws.length === 0 ? (
        <Typography sx={{ fontSize: 15, color: BODY, lineHeight: 1.6 }}>
          No promo draw has been made yet — the first winner will be listed here.
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {draws.map((d, i) => {
            const { tokens, packs, custom } = rewardParts(d.rewards);
            const won: Array<{ text: string; colour: string }> = [
              tokens !== null
                ? { text: `${tokens.toLocaleString('en-AU')} INC ${tokens === 1 ? 'token' : 'tokens'}`, colour: TOKENS_COLOUR }
                : null,
              packs ? { text: `${packs.count.toLocaleString('en-AU')} × ${packs.name}`, colour: PACKS_COLOUR } : null,
              custom ? { text: custom, colour: CUSTOM_COLOUR } : null
            ].filter((w): w is { text: string; colour: string } => w !== null);
            return (
              <Box
                component="li"
                key={d.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '150px minmax(0,1fr)' },
                  gap: { xs: 0.5, sm: 2 },
                  py: 1.75,
                  borderTop: i === 0 ? 'none' : `1px solid ${LINE}`
                }}
              >
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: MUTED }}>
                  {week(d.endDate)}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 900, color: INK, overflowWrap: 'anywhere' }}>
                    {d.winners
                      .map((w) => [w.firstName, w.lastInitial ? `${w.lastInitial}.` : ''].filter(Boolean).join(' '))
                      .join(' & ')}
                  </Typography>
                  {won.length > 0 && (
                    <Typography sx={{ mt: 0.25, fontSize: 14, color: BODY, lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                      Won{' '}
                      {won.map((w, j) => (
                        <Fragment key={`${j}:${w.text}`}>
                          {j > 0 && <Box component="span" sx={{ color: MUTED }}>{' · '}</Box>}
                          <Box component="span" sx={{ color: w.colour, fontWeight: 800 }}>
                            {w.text}
                          </Box>
                        </Fragment>
                      ))}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  </Box>
);

export default PreviousPromoWinners;
